import {
  tickValues,
  type PaletteColour,
  type ScaleConfig,
  type ValueWord,
} from '@anytracker/core'

import { IconButton } from '../../components/Button.js'
import { TextField } from '../../components/Field.js'
import { Icon, ICONS } from '../../components/Layout.js'
import styles from './TrackEditor.module.css'

interface Props {
  readonly scale: ScaleConfig
  readonly words: readonly ValueWord[]
  /** The track's colour, which the bands are drawn in. */
  readonly colour: PaletteColour
  readonly onChange: (words: readonly ValueWord[]) => void
}

/**
 * Naming the points on a scale (T-807).
 *
 * **No design exists for this.** T-807 put authoring out of scope on the
 * grounds that no track editor was drawn at all, which was true of the first
 * handoff and is not true now — the editor exists, T-911 redraws it, and the
 * alternative was shipping words that could only arrive through a restored
 * backup. The shape here is inferred from the idiom the redesign already uses
 * for enum options: rows, plus a dashed "add another". Recorded in
 * `docs/design-issues.md`.
 *
 * Rows stay in the order they were added rather than sorting themselves by
 * position. A list that reorders under the cursor while a number is being typed
 * is unusable, and `wordFor` does not care about order.
 */
/** How strong each word's band is, in order along the scale. */
const STRENGTHS = [22, 40, 58, 76, 100] as const

function bandColour(colour: PaletteColour, rank: number): string {
  if (rank < 0) {
    return 'var(--rail)'
  }
  if (rank >= STRENGTHS.length) {
    return `var(--hue-${colour}-deep)`
  }
  return `color-mix(in srgb, var(--hue-${colour}-fill) ${String(STRENGTHS[rank] ?? 100)}%, var(--surface))`
}

export function ValueWordsEditor({ scale, words, colour, onChange }: Props) {
  function update(index: number, changes: Partial<ValueWord>) {
    onChange(words.map((word, at) => (at === index ? { ...word, ...changes } : word)))
  }

  /**
   * Where a new word starts.
   *
   * The midpoint for the first, then halfway between the last one and the top —
   * so adding three words on a 1–10 scale lands them somewhere plausible rather
   * than stacking them all at the minimum, which would be three validation
   * failures for the crime of pressing the button twice.
   */
  function nextPoint(): number {
    const highest = words.reduce((top, word) => Math.max(top, word.at), scale.min)
    const candidate =
      words.length === 0 ? (scale.min + scale.max) / 2 : (highest + scale.max) / 2
    return Number(candidate.toFixed(scale.decimals))
  }

  /*
   * Each word covers its own point up to the next one, so the preview colours
   * every value by the rank of the word that covers it. Decoration: the rows
   * below say the same thing in words, so it is hidden from assistive tech.
   */
  const ranked = words
    .map((word, index) => ({ ...word, index }))
    .filter((word) => Number.isFinite(word.at))
    .sort((a, b) => a.at - b.at)
  const rankOf = new Map(ranked.map((word, rank) => [word.index, rank]))
  const ticks = tickValues(scale)
  const cells =
    ticks.length <= 21
      ? ticks.map((value) => {
          let rank = -1
          ranked.forEach((word, at) => {
            if (word.at <= value) {
              rank = at
            }
          })
          return { value, rank, starts: ranked.some((word) => word.at === value) }
        })
      : []

  return (
    <section className={styles['wordsSection']} data-bleed>
      <div className={styles['wordsIntro']}>
        <h2 id="words-heading" className={styles['heading']}>
          Words along the scale
        </h2>
        <p className={styles['wordsHint']}>
          Optional. Name a few points and the logging screen shows the word beside the
          number — a screen reader reads it out too. Each word covers its point up to the
          next one.
        </p>
      </div>

      {cells.length > 0 && (
        <div className={styles['bandPreview']} aria-hidden="true">
          <div
            className={styles['cells']}
            style={{
              gridTemplateColumns: `repeat(${String(cells.length)}, minmax(0, 1fr))`,
            }}
          >
            {cells.map((cell) => (
              <span
                key={cell.value}
                className={styles['cell']}
                style={{ background: bandColour(colour, cell.rank) }}
              />
            ))}
          </div>
          <div
            className={styles['cells']}
            style={{
              gridTemplateColumns: `repeat(${String(cells.length)}, minmax(0, 1fr))`,
            }}
          >
            {cells.map((cell) => (
              <span
                key={cell.value}
                className={cell.starts ? styles['cellNumberStart'] : styles['cellNumber']}
              >
                {cell.value.toFixed(scale.decimals)}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className={styles['wordList']}>
        {words.map((word, index) => (
          // Index as the key, deliberately: a word has no id, and two words can
          // legitimately hold the same text while one is being retyped — so
          // neither the label nor the point is stable enough to key on.
          <div key={index} className={styles['wordRow']}>
            <span
              className={styles['wordBand']}
              style={{ background: bandColour(colour, rankOf.get(index) ?? -1) }}
              aria-hidden="true"
            />
            <TextField
              label="At"
              type="number"
              inputMode="decimal"
              value={String(word.at)}
              onChange={(event) => {
                const parsed = Number(event.target.value)
                update(index, { at: Number.isFinite(parsed) ? parsed : word.at })
              }}
            />
            <TextField
              label="Word"
              value={word.label}
              placeholder="Rough, fine, good…"
              onChange={(event) => update(index, { label: event.target.value })}
            />
            <IconButton
              label={`Remove ${word.label.trim() === '' ? 'this word' : word.label}`}
              onClick={() => onChange(words.filter((_, at) => at !== index))}
            >
              <Icon path={ICONS.close} />
            </IconButton>
          </div>
        ))}

        <button
          type="button"
          className={styles['addWord']}
          onClick={() => onChange([...words, { at: nextPoint(), label: '' }])}
        >
          <Icon path={ICONS.plus} />
          Add a word
        </button>
      </div>
    </section>
  )
}
